/**
 * FirebaseBookingPage.jsx — Full Firebase booking flow
 *
 * Orchestrates the three-step booking process:
 *   Step 1: SessionPicker — choose session + seats
 *   Step 2: PaymentForm   — customer info + Square payment
 *   Step 3: Confirm       — booking confirmation
 *
 * Fetches the class card from Firestore by document ID (from URL param).
 * Passes accent color from the card's template for consistent styling.
 *
 * Route: /booking/:slug
 */
import { useState, useEffect, useRef } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import SessionPicker from './SessionPicker';
import CustomerInfoForm from './CustomerInfoForm';
import ClassIcon from './icons/ClassIcon';
import RequestForm from './RequestForm';

import { TEMPLATE_TO_CLASS_ICON } from '../data/defaultTemplates';
import { useLocations } from '../services/firestore';

// Maps private template IDs to their RequestForm classType
const PRIVATE_CLASS_TYPE = {
  'pottery-party': 'party',
  'wheel-throwing': 'wheel',
};

// Accent color from card.blobColor (set by admin); fallback palette by templateId
const TEMPLATE_ACCENT = {
  'bowl-workshop': '#E8A020',
  'planter-workshop': '#4A6FA5',
  'vase-workshop': '#B55A3A',
  'mug-workshop': '#4A7A5C',
  'pottery-party': '#7A3A5A',
  'wheel-throwing': '#3A7A7A',
};

export default function FirebaseBookingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // If navigated from ClassGrid, the card object is already in memory — use it
  // immediately so the page shows without any Firestore round trip.
  // Direct links (bookmarks, admin share links) won't have state, so we fall
  // back to the Firestore fetch in that case.
  const stateCard = location.state?.card ?? null;

  const [card, setCard] = useState(stateCard);
  const [loading, setLoading] = useState(!stateCard);
  const [error, setError] = useState(null);

  const { locations } = useLocations();

  // Booking flow state
  const [step, setStep] = useState('session'); // 'session' | 'info' | 'confirm' | 'inquiry'
  const [isWaitlistMode, setIsWaitlistMode] = useState(false);
  const stepsRef = useRef(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [bookingData, setBookingData] = useState(null);

  // Fetch class card from Firestore — only needed when no card was passed via
  // route state (direct link, bookmark, admin share link, etc.)
  useEffect(() => {
    if (stateCard) return; // already have the card — skip the fetch

    async function fetchCard() {
      try {
        // Try slug lookup first
        const q = query(
          collection(db, 'classCards'),
          where('slug', '==', slug),
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setCard({ id: d.id, ...d.data() });
          return;
        }
        // Fallback: treat the URL param as a doc ID (cards not yet re-saved with slug)
        const docSnap = await getDoc(doc(db, 'classCards', slug));
        if (docSnap.exists()) {
          setCard({ id: docSnap.id, ...docSnap.data() });
          return;
        }
        setError('Class not found.');
      } catch (err) {
        console.error('Failed to load class card:', err);
        setError('Unable to load class details.');
      } finally {
        setLoading(false);
      }
    }

    if (slug) fetchCard();
  }, [slug, stateCard]);

  const accent = card
    ? card.blobColor || TEMPLATE_ACCENT[card.templateId] || '#9C7A3C'
    : '#9C7A3C';

  const classId = card ? TEMPLATE_TO_CLASS_ICON[card.templateId] || 1 : 1;
  const privateClassType = card
    ? PRIVATE_CLASS_TYPE[card.templateId] || null
    : null;

  // ?book=true lets admin bypass the inquiry form and share a direct booking link
  const directBook = searchParams.get('book') === 'true';
  const showInquiryForm = privateClassType && !directBook;

  // Sold-out callback — switch to inquiry view
  const handleSoldOutInquire = () => {
    setStep('inquiry');
    scrollToSteps();
  };

  // Step handlers
  const scrollToSteps = () =>
    stepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleSessionSelect = ({ session, seats }) => {
    setSelectedSession(session);
    setSelectedSeats(seats);
    setStep('info');
    scrollToSteps();
    if (analytics)
      logEvent(analytics, 'begin_checkout', {
        item_name: card?.title,
        session_date: session?.date,
        seats,
        value: (card?.price || 0) * seats,
        currency: 'USD',
      });
  };

  const handleBookingSuccess = (booking) => {
    setBookingData(booking);
    setStep('confirm');
    scrollToSteps();
    if (analytics)
      logEvent(analytics, 'purchase', {
        item_name: booking.cardTitle,
        session_date: booking.sessionDate,
        seats: booking.seats,
        value: booking.totalPrice,
        currency: 'USD',
        transaction_id: booking.bookingId,
      });
  };

  const handleBackToSessions = () => {
    setStep('session');
    setSelectedSession(null);
    setSelectedSeats(1);
    scrollToSteps();
  };

  const handleReset = () => {
    navigate('/', { state: { scrollTo: 'classes' } });
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <p style={styles.loadingText}>Loading class details...</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (error || !card) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error || 'Class not found.'}</p>
          <button onClick={handleReset} style={styles.backBtn}>
            Back to Classes
          </button>
        </div>
      </div>
    );
  }

  // ── Confirmation step ──
  if (step === 'confirm' && bookingData) {
    return (
      <div style={styles.page}>
        <div style={styles.classHeader}>
          <div style={styles.confirmBox}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
            <h2 style={{ ...styles.classTitle, marginBottom: 8 }}>
              You&apos;re Booked!
            </h2>
            <p style={styles.confirmMsg}>
              Hey {bookingData.customerName}, your spot is confirmed. We&apos;ll
              send details to <strong>{bookingData.customerEmail}</strong>.
            </p>
            <p style={styles.confirmNote}>See you there!</p>
            <button
              onClick={handleReset}
              style={{ ...styles.backLink, marginTop: 24 }}
            >
              ← Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Derive studio + city from location (supports "Studio · City" format)
  const [studioName, cityName] = (card.location || '').split(' · ');

  // Look up address for the studio location (enables map link)
  const locationRecord = locations.find((l) => l.name === studioName);
  const locationAddress = locationRecord?.address || null;
  const mapUrl = locationAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationAddress)}`
    : null;

  // ── Session picker + Payment steps ──
  return (
    <div style={styles.page}>
      <div className="section-pad" style={{ paddingTop: 48 }}>
        {/* Back link */}
        <div style={{ marginBottom: 32 }}>
          <button onClick={handleReset} style={styles.backLink}>
            ← Back to classes
          </button>
        </div>

        {/* Class identity tile — bordered with accent, matches production */}
        <div
          className="tile fade"
          style={{
            padding: 'clamp(32px, 5vh, 48px) clamp(24px, 5vw, 60px)',
            textAlign: 'center',
            marginBottom: 24,
            border: `4px solid ${accent}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: `${accent}18`,
                border: `1.5px solid ${accent}55`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ClassIcon classId={classId} color={accent} size={28} />
            </div>
            {card.tag && (
              <span
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  color: accent,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  whiteSpace: 'pre-line',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {card.tag}
              </span>
            )}
          </div>

          <h1 style={styles.classTitle}>{card.title.toUpperCase()}</h1>

          {/* tagline — long description from CLASS DETAILS */}
          {card.tagline &&
            card.tagline.split('\n\n').map((para, i) => (
              <p key={i} style={styles.classSub}>
                {para}
              </p>
            ))}
        </div>

        {/* Class details tile — matches production .booking-details-grid layout */}
        {(card.location ||
          card.details?.whatsIncluded?.length > 0 ||
          card.details?.rulesAndPolicy?.length > 0) && (
          <div
            className="tile"
            style={{
              padding: '32px clamp(20px, 5vw, 40px)',
              border: 'none',
              background: '#F9F2E9',
              position: 'relative',
              zIndex: 51,
              isolation: 'isolate',
              marginBottom: 24,
            }}
          >
            <div>
              <div
                className="caption"
                style={{ marginBottom: 20, fontSize: '0.8rem' }}
              >
                Class Details
              </div>

              {/* Studio row */}
              {studioName && (
                <div style={styles.detailRow}>
                  <span className="caption">Studio</span>
                  {mapUrl ? (
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontFamily: "'Raleway', sans-serif",
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: '#9C7A3C',
                        minWidth: 0,
                        textAlign: 'right',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {studioName}
                      <svg
                        width="9"
                        height="11"
                        viewBox="0 0 9 11"
                        fill="currentColor"
                        style={{ opacity: 0.6, flexShrink: 0 }}
                      >
                        <path d="M4.5 0C2.02 0 0 2.02 0 4.5c0 3.38 4.5 6.5 4.5 6.5S9 7.88 9 4.5C9 2.02 6.98 0 4.5 0zm0 6.25c-.97 0-1.75-.78-1.75-1.75S3.53 2.75 4.5 2.75 6.25 3.53 6.25 4.5 5.47 6.25 4.5 6.25z" />
                      </svg>
                    </a>
                  ) : (
                    <span
                      style={{
                        fontFamily: "'Raleway', sans-serif",
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: '#9C7A3C',
                        minWidth: 0,
                        textAlign: 'right',
                      }}
                    >
                      {studioName}
                    </span>
                  )}
                </div>
              )}

              {/* Location / city row */}
              {cityName && (
                <div style={styles.detailRow}>
                  <span className="caption">Location</span>
                  <span
                    className="display"
                    style={{
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      minWidth: 0,
                      flexShrink: 1,
                    }}
                  >
                    {cityName}
                    <svg
                      width="9"
                      height="11"
                      viewBox="0 0 9 11"
                      fill="currentColor"
                      style={{ opacity: 0.5, flexShrink: 0 }}
                    >
                      <path d="M4.5 0C2.02 0 0 2.02 0 4.5c0 3.38 4.5 6.5 4.5 6.5S9 7.88 9 4.5C9 2.02 6.98 0 4.5 0zm0 6.25c-.97 0-1.75-.78-1.75-1.75S3.53 2.75 4.5 2.75 6.25 3.53 6.25 4.5 5.47 6.25 4.5 6.25z" />
                    </svg>
                  </span>
                </div>
              )}

              {/* Design Options */}
              {card.details?.designOptions?.length > 0 && (
                <div style={{ ...styles.detailRow, alignItems: 'flex-start' }}>
                  <span className="caption" style={{ whiteSpace: 'nowrap' }}>
                    Design Options
                  </span>
                  <span
                    style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#1a1208',
                      whiteSpace: 'pre-wrap',
                      textAlign: 'right',
                      minWidth: 0,
                    }}
                  >
                    {card.details.designOptions
                      .map((item) => `• ${item}`)
                      .join('\n')}
                  </span>
                </div>
              )}

              {/* Special Guest */}
              {card.details?.specialGuest && (
                <div style={styles.detailRow}>
                  <span className="caption" style={{ whiteSpace: 'nowrap' }}>
                    Special Guest
                  </span>
                  <span
                    style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#1a1208',
                      textAlign: 'right',
                      minWidth: 0,
                    }}
                  >
                    {card.details.specialGuest}
                  </span>
                </div>
              )}

              {/* What's Included */}
              {card.details?.whatsIncluded?.length > 0 && (
                <div style={{ ...styles.detailRow, alignItems: 'flex-start' }}>
                  <span className="caption" style={{ whiteSpace: 'nowrap' }}>
                    What&apos;s Included
                  </span>
                  <span
                    style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#1a1208',
                      whiteSpace: 'pre-wrap',
                      textAlign: 'right',
                      minWidth: 0,
                    }}
                  >
                    {card.details.whatsIncluded
                      .map((item) => `• ${item}`)
                      .join('\n')}
                  </span>
                </div>
              )}
            </div>

            {/* Rules / policy notes */}
            {card.details?.rulesAndPolicy?.length > 0 && (
              <div style={styles.rulesNotes}>
                {card.details.rulesAndPolicy.map((rule) => (
                  <div key={rule}>{rule}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Private class → inquiry form (unless ?book=true); Public class → step wizard */}
        {showInquiryForm ? (
          <div
            style={{
              ...styles.stepContent,
              padding: '32px 28px',
              background: '#F9F2E9',
            }}
          >
            <RequestForm
              formId={card.inquiryFormId || card.templateId}
              className={card.title}
            />
          </div>
        ) : (
          <>
            {/* Step indicator — hidden when SessionPicker shows waitlist form */}
            {!isWaitlistMode && (
              <div ref={stepsRef} style={styles.steps}>
                <StepDot
                  number={1}
                  label="Session"
                  active={step === 'session'}
                  done={step === 'info'}
                  accent={accent}
                />
                <div style={styles.stepLine} />
                <StepDot
                  number={2}
                  label="Your Details"
                  active={step === 'info'}
                  done={false}
                  accent={accent}
                />
              </div>
            )}

            {/* Step content */}
            <div style={styles.stepContent}>
              {step === 'session' && (
                <SessionPicker
                  cardId={card.id}
                  accent={accent}
                  card={card}
                  onSelect={handleSessionSelect}
                  onInquire={handleSoldOutInquire}
                  onWaitlist={() => setIsWaitlistMode(true)}
                />
              )}

              {step === 'info' && selectedSession && (
                <CustomerInfoForm
                  card={card}
                  session={selectedSession}
                  seats={selectedSeats}
                  accent={accent}
                  onSuccess={handleBookingSuccess}
                  onBack={handleBackToSessions}
                />
              )}

              {step === 'inquiry' && (
                <div style={{ padding: '32px 28px', background: '#F9F2E9' }}>
                  <RequestForm
                    formId={card.inquiryFormId || card.templateId}
                    className={card.title}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Step indicator dot ──
function StepDot({ number, label, active, done, accent }) {
  const bg = active ? accent : done ? `${accent}88` : '#DDD8CC';
  const textColor = active || done ? '#fff' : '#999';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: textColor,
          fontFamily: "'Raleway', sans-serif",
          transition: 'background 0.3s',
        }}
      >
        {done ? '✓' : number}
      </div>
      <span
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: '0.75rem',
          color: active ? '#1a1208' : '#999',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          transition: 'color 0.3s',
        }}
      >
        {label}
      </span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100vh - 74px)',
    background: '#F9F2E9',
    padding: '24px 0',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '40vh',
  },
  loadingText: {
    fontFamily: "'Raleway', sans-serif",
    color: '#9C7A3C',
    fontSize: '0.9rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '40vh',
    gap: 20,
  },
  errorText: {
    fontFamily: "'Raleway', sans-serif",
    color: '#B55A3A',
    fontSize: '0.9rem',
  },
  backBtn: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#9C7A3C',
    background: 'transparent',
    border: '1.5px solid #9C7A3C',
    borderRadius: 50,
    padding: '10px 24px',
    cursor: 'pointer',
  },
  backLink: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#9C7A3C',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 24,
    display: 'block',
  },
  classTitle: {
    fontFamily: "'Titan One', sans-serif",
    fontWeight: 400,
    fontSize: 'clamp(2rem, 6vw, 3.5rem)',
    lineHeight: 0.9,
    letterSpacing: '-0.01em',
    color: '#1a1208',
    marginBottom: 16,
  },
  classSub: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
    color: '#9C7A3C',
    marginBottom: 10,
    lineHeight: 1.6,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '13px 0',
    borderBottom: '1px solid #DDD8CC',
  },
  rulesNotes: {
    paddingTop: 16,
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.75rem',
    color: '#9C7A3C',
    lineHeight: 1.8,
  },
  steps: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 8,
  },
  stepLine: {
    width: 40,
    height: 2,
    background: '#DDD8CC',
    borderRadius: 1,
  },
  stepContent: {
    maxWidth: 520,
    margin: '0 auto',
    background: '#FCF7F1',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(26,18,8,0.06)',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 51,
    isolation: 'isolate',
  },
  confirmBox: {
    textAlign: 'center',
    padding: '48px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  confirmMsg: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.9rem',
    color: '#5a4a30',
    lineHeight: 1.6,
    maxWidth: 360,
    margin: '0 auto 8px',
  },
  confirmNote: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.8rem',
    color: '#9C7A3C',
    fontStyle: 'italic',
  },
};
